import './DeathScreen.css';

export interface DeathScreenCallbacks {
  onRevive: () => void;
}

const TOTAL_CIRCUMFERENCE = 213.6; // 2 * PI * 34

export class DeathScreen {
  private readonly container: HTMLElement;
  private readonly timerNumEl: HTMLElement;
  private readonly timerRingEl: HTMLElement;
  private readonly timerWrapEl: HTMLElement;
  private readonly btnRevive: HTMLButtonElement;
  private readonly btnTextEl: HTMLElement;
  private readonly hintEl: HTMLElement;

  private onReviveCallback: (() => void) | null = null;
  private countdownTimer: number | null = null;
  private totalSeconds = 3;
  private remainingSeconds = 3;
  private isReady = false;
  private isVisible = false;

  constructor(parent: HTMLElement = document.body) {
    this.container = document.createElement('div');
    this.container.id = 'death-screen';

    this.container.innerHTML = `
      <div class="death-content">
        <div class="death-crest-wrap">
          <svg class="death-crest" viewBox="0 0 64 64" fill="none">
            <defs>
              <linearGradient id="crest-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#ff6666"/>
                <stop offset="100%" stop-color="#991111"/>
              </linearGradient>
            </defs>
            <path d="M32 4 L52 20 L32 60 L12 20 Z" stroke="url(#crest-grad)" stroke-width="1.8" stroke-linejoin="round" fill="rgba(180,20,20,0.1)"/>
            <path d="M32 12 L44 22 L32 50 L20 22 Z" stroke="url(#crest-grad)" stroke-width="1.2" opacity="0.65"/>
            <circle cx="32" cy="22" r="3.5" fill="#ff4d4d"/>
            <path d="M14 14 L50 50 M50 14 L14 50" stroke="#ff3333" stroke-width="1.4" stroke-linecap="round" opacity="0.35"/>
          </svg>
        </div>

        <h1 class="death-title">ZEMŘEL JSI</h1>

        <div class="death-divider">
          <span class="death-line"></span>
          <span class="death-diamond">◆</span>
          <span class="death-line"></span>
        </div>

        <p class="death-subtitle">PADL JSI V BOJI</p>
        
        <div class="death-timer-wrap" id="death-timer-wrap">
          <svg class="death-timer-ring" viewBox="0 0 80 80">
            <circle class="ring-bg" cx="40" cy="40" r="34" />
            <circle class="ring-progress" id="death-ring-progress" cx="40" cy="40" r="34" />
          </svg>
          <div class="death-timer-num" id="death-timer">3</div>
        </div>

        <button class="death-btn-revive" id="death-btn-revive" disabled>
          <span class="death-btn-text" id="death-btn-text">OŽIVENÍ ZA 3 S</span>
        </button>

        <div class="death-hint" id="death-hint">
          <span>Stiskni <kbd class="death-key">MEZERNÍK</kbd> nebo <kbd class="death-key">ENTER</kbd></span>
        </div>
      </div>
    `;

    parent.appendChild(this.container);

    this.timerWrapEl = this.container.querySelector('#death-timer-wrap') as HTMLElement;
    this.timerNumEl = this.container.querySelector('#death-timer') as HTMLElement;
    this.timerRingEl = this.container.querySelector('#death-ring-progress') as HTMLElement;
    this.btnRevive = this.container.querySelector('#death-btn-revive') as HTMLButtonElement;
    this.btnTextEl = this.container.querySelector('#death-btn-text') as HTMLElement;
    this.hintEl = this.container.querySelector('#death-hint') as HTMLElement;

    this.btnRevive.addEventListener('click', () => {
      if (this.isReady) {
        this.triggerRevive();
      }
    });

    window.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.isVisible || !this.isReady) return;
    if (e.code === 'Space' || e.code === 'Enter' || e.code === 'KeyE') {
      e.preventDefault();
      e.stopPropagation();
      this.triggerRevive();
    }
  };

  show(onRevive: () => void, countdownSeconds = 3): void {
    this.onReviveCallback = onRevive;
    this.totalSeconds = countdownSeconds;
    this.remainingSeconds = countdownSeconds;
    this.isReady = false;
    this.isVisible = true;

    // Clear any previous interval
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    // Reset UI state
    this.timerWrapEl.style.display = 'flex';
    this.timerNumEl.textContent = String(this.remainingSeconds);
    this.timerRingEl.style.strokeDashoffset = '0';
    this.btnRevive.disabled = true;
    this.btnRevive.classList.remove('ready');
    this.btnTextEl.textContent = `OŽIVENÍ ZA ${this.remainingSeconds} S`;
    this.hintEl.classList.remove('visible');

    // Show container
    this.container.classList.add('visible');

    // Release pointer lock so player can see cursor and click
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }

    // Countdown tick
    this.countdownTimer = window.setInterval(() => {
      this.remainingSeconds--;

      if (this.remainingSeconds > 0) {
        this.timerNumEl.textContent = String(this.remainingSeconds);
        this.timerNumEl.classList.add('tick');
        setTimeout(() => this.timerNumEl.classList.remove('tick'), 200);

        const progress = (this.totalSeconds - this.remainingSeconds) / this.totalSeconds;
        this.timerRingEl.style.strokeDashoffset = String(progress * TOTAL_CIRCUMFERENCE);

        this.btnTextEl.textContent = `OŽIVENÍ ZA ${this.remainingSeconds} S`;
      } else {
        // Countdown finished — enable Revive button
        if (this.countdownTimer !== null) {
          window.clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
        this.isReady = true;
        this.timerWrapEl.style.display = 'none';
        this.btnRevive.disabled = false;
        this.btnRevive.classList.add('ready');
        this.btnTextEl.textContent = 'OŽIVIT POSTAVU';
        this.hintEl.classList.add('visible');
      }
    }, 1000);
  }

  private triggerRevive(): void {
    if (!this.isVisible) return;
    this.hide();
    const cb = this.onReviveCallback;
    this.onReviveCallback = null;
    cb?.();
  }

  hide(): void {
    this.isVisible = false;
    this.isReady = false;
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.container.classList.remove('visible');
  }

  dispose(): void {
    this.hide();
    window.removeEventListener('keydown', this.onKeyDown);
    this.container.remove();
  }
}
